// EditCathayIdScreen.jsx
// =====================================================================
// Dedicated edit screen for fixing the CathayID on the currently-open
// session WITHOUT signing out. Reached from the ActiveSessionScreen's
// "Wrong CathayID? Fix it" link. On submit it calls
// POST /api/sessions/:client_uuid/edit-cathay-id (server-side IDOR
// guard binds the update to req.device.id) and returns to the active
// session screen on success.
// =====================================================================

import { useState } from 'react';
import Header from '../components/Header.jsx';
import AlphaPad from '../components/AlphaPad.jsx';
import { api } from '../lib/api.js';
import { setActiveSession } from '../lib/storage.js';

const MIN_LEN = 4;
const MAX_LEN = 10;

export default function EditCathayIdScreen({ device, session, onSaved, onCancel }) {
  const [cathayId, setCathayId] = useState(session?.cathay_id_entered || '');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const id = cathayId.trim().toUpperCase();
    if (id.length < MIN_LEN || id.length > MAX_LEN) {
      setErr(`CathayID must be ${MIN_LEN}–${MAX_LEN} letters`);
      return;
    }
    if (!/^[A-Z]+$/.test(id)) {
      setErr('CathayID must contain letters only');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await api.editCathayId(session.client_uuid, id);
      // Server returns the updated row; cache it locally so the
      // ActiveSessionScreen shows the new CathayID immediately.
      setActiveSession(r.session);
      onSaved(r.session);
    } catch (e) {
      setErr(e.message || 'Could not save CathayID');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      <Header deviceName={device?.name} />
      <main className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-2xl space-y-6">
          <header className="text-center space-y-1">
            <h1 className="text-kiosk-lg font-semibold text-cathay-700">Edit CathayID</h1>
            <p className="text-kiosk text-slate-500">
              Fix a wrong CathayID without signing out of the session.
            </p>
          </header>

          <div className="bg-white rounded-2xl px-6 py-5 text-center font-mono tracking-[0.3em] text-kiosk-xl text-slate-900 min-h-[5rem] flex items-center justify-center border border-slate-200">
            {cathayId || <span className="text-slate-300">CathayID</span>}
          </div>

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-kiosk">
              {err}
            </div>
          )}

          <AlphaPad
            value={cathayId}
            maxLen={MAX_LEN}
            onKey={(k) => setCathayId((s) => s + k)}
            onBackspace={() => setCathayId((s) => s.slice(0, -1))}
            onClear={() => setCathayId('')}
          />

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-kiosk-lg font-semibold py-5 rounded-2xl"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || cathayId.length < MIN_LEN}
              className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-50 text-white text-kiosk-lg font-semibold py-5 rounded-2xl shadow"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
